export type EstadoOportunidade = 'aberta' | 'ganha' | 'perdida';
export type TipoEtapa = 'aberta' | 'ganha' | 'perdida';
/** CRM no novo desenho (lote 2): e-mail, WhatsApp, visita e almoço são os tipos do RD. */
export type TipoAtividade = 'tarefa' | 'ligacao' | 'reuniao' | 'follow_up' | 'email' | 'whatsapp' | 'visita' | 'almoco';

export interface Etapa {
  id: string;
  funil_id: string;
  nome: string;
  ordem: number;
  probabilidade: number;
  tipo: TipoEtapa;
  /** Total real da etapa (não só o que coube no quadro). */
  oportunidades: number;
  valor_centavos: number;
  /** Quantas oportunidades desta etapa vieram no quadro (só no `/pipeline/quadro`). */
  exibidas?: number;
}

/** Trilha 4 (ciclo do cliente): recorrência dos itens da oportunidade (uma para todos). */
export const RECORRENCIAS = ['mensal', 'trimestral', 'semestral', 'anual', 'avulso'] as const;
export type Recorrencia = (typeof RECORRENCIAS)[number];

export interface ItemOportunidade {
  id: string;
  servico_id: string | null;
  servico_nome: string | null;
  descricao: string;
  quantidade: number;
  preco_centavos: number;
  recorrencia: Recorrencia;
  /** V3 verticais: a vertical (empresa) do item; o ganho cria um contrato por empresa. */
  vertical_id?: string | null;
  /** CRM lote 4: desconto do item e como foi digitado. */
  desconto_centavos?: number;
  desconto_tipo?: 'percentual' | 'valor';
  desconto_percentual?: number | null;
}

export interface Oportunidade {
  id: string;
  /** Trilha 4: só no detalhe. */
  itens?: ItemOportunidade[];
  titulo: string;
  parceiro_id: string;
  parceiro_nome: string | null;
  /** V3 verticais: as verticais em que o lead é trabalhado (vazia = "Todas"). */
  empresas?: string[];
  valor_centavos: number;
  moeda: string;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  equipe_id: string | null;
  equipe_nome: string | null;
  etapa_id: string;
  etapa_nome: string;
  etapa_tipo: TipoEtapa;
  /** U13: funil da etapa. */
  funil_id: string;
  funil_nome: string;
  /** U5: parceiro que indicou. */
  indicado_por_id: string | null;
  indicado_por_nome: string | null;
  /** Lote crm5 (E10): regime tributário pretendido (`simples`, `mei`, `presumido`, `real`) ou null. */
  regime_pretendido?: string | null;
  probabilidade: number;
  origem: string;
  canal: string | null;
  utm_fonte: string | null;
  utm_midia: string | null;
  utm_campanha: string | null;
  previsao_fechamento: string | null;
  motivo_perda_id: string | null;
  motivo_perda_nome: string | null;
  ganha_em: string | null;
  perdida_em: string | null;
  projeto_id: string | null;
  estado: EstadoOportunidade;
  extras: Record<string, unknown>;
  atividades_pendentes: number;
  atividades_atrasadas: number;
  /** CRM lote 3. */
  qualificacao?: number;
  pausada_em?: string | null;
  /** Lista de 30/10 (B), item 21: a original desta cópia; o título só quando quem lê vê a original. */
  copiada_de_id?: string | null;
  copiada_de_titulo?: string | null;
  created_at: string;
  updated_at?: string;
}

export interface ConversaoEtapa {
  etapa_id: string;
  nome: string;
  entraram: number;
  avancaram: number;
  taxa: number | null;
}

/** U13: funil do Pipeline (o padrão é o "Modelo padrão"). */
export interface Funil {
  id: string;
  nome: string;
  padrao: boolean;
  arquivado_em: string | null;
  etapas: number;
  abertas: number;
}

export interface Quadro {
  funil: Pick<Funil, 'id' | 'nome' | 'padrao' | 'arquivado_em'>;
  etapas: Etapa[];
  oportunidades: Oportunidade[];
  cartoes: { exibidos: number; total: number; limite: number; limite_por_etapa: number; truncado: boolean };
  metricas: {
    abertas: number;
    valor_em_negociacao_centavos: number;
    valor_ponderado_centavos: number;
    conversao_etapas: ConversaoEtapa[];
    ciclo_medio_dias: number | null;
    ganhas: number;
  };
}

export interface Atividade {
  id: string;
  oportunidade_id: string;
  oportunidade_titulo: string;
  parceiro_id: string;
  parceiro_nome: string | null;
  tipo: TipoAtividade;
  titulo: string;
  data_hora: string;
  responsavel_id: string | null;
  responsavel_nome: string | null;
  estado: 'pendente' | 'concluida' | 'cancelada';
  atrasada: boolean;
  concluida_em: string | null;
  agendamento_id: string | null;
  /** CRM lote 2. */
  descricao?: string | null;
  ata?: string | null;
  ata_anexo_ids?: string[];
  responsaveis?: { id: string; nome: string }[];
  oportunidade_valor_centavos?: number;
  oportunidade_moeda?: string;
  oportunidade_funil_id?: string | null;
  oportunidade_etapa_nome?: string | null;
}

export interface Motivo {
  id: string;
  nome: string;
  perdidas: number;
}

export interface Equipe {
  id: string;
  nome: string;
  membros: { id: string; nome: string }[];
  abertas: number;
  valor_aberto_centavos: number;
}

export interface Meta {
  id: string;
  alvo: 'equipe' | 'vendedor';
  alvo_id: string;
  alvo_nome: string;
  mes: string;
  valor_centavos: number;
  moeda: string;
  realizado_centavos: number;
  percentual: number | null;
}

export const TIPOS_ATIVIDADE: TipoAtividade[] = ['tarefa', 'ligacao', 'reuniao', 'follow_up', 'email', 'whatsapp', 'visita', 'almoco'];

/** Converte o valor de um input datetime-local (hora do navegador) para ISO 8601. */
export const paraIso = (local: string) => new Date(local).toISOString();
